import {
  $isElementNode,
  $isTextNode,
  type LexicalNode,
  type TextNode,
} from 'lexical';

import { $isFontSizeNode, type FontSizeNode } from './FontSizeNode';

// Lexical's text-format adjacency-skip in `exportTextFormat` walks transparently
// through inline elements (including FontSizeNode) to find the next text node.
// When a format span (bold, italic, etc.) crosses a FontSizeNode boundary,
// Lexical skips emitting the closing tag inside [/size] *and* the opening tag
// outside, producing markdown like `[size=N]**X[/size] Y**` (opening inside,
// closing outside) or `**X [size=N]Y[/size]` (opening outside, no closing at
// all when `unclosableTags` suppresses the inner close).
//
// On reimport, the FONT_SIZE text-match transformer wins outermost-match
// precedence over BOLD_STAR (because [size=N]…[/size] starts before the bold
// opening), swallows one of the `**` markers, and the orphan renders literally.
//
// This module detects per-format leaks across a FontSizeNode boundary so the
// transformer can inject balanced markers around the inner content. See
// FontSizePlugin.test.ts > "bold spanning FontSize boundary" for the
// regression cases.

// Ordered to match @lexical/markdown's TEXT_FORMAT_TRANSFORMERS opening order
// for single-format transformers (after filtering out the ones with multiple
// formats and HIGHLIGHT, which we replace with a custom text-match transformer).
const FORMATS_IN_OPEN_ORDER: ReadonlyArray<readonly [string, string]> = [
  ['code', '`'],
  ['bold', '**'],
  ['italic', '*'],
  ['strikethrough', '~~'],
];

export interface BoundaryFormatLeak {
  /** Markers to emit before `[size=N]` to close any outer format that leaks in. */
  outerPrefix: string;
  /** Markers to emit immediately after `[size=N]` to reopen leaked-in formats. */
  innerOpen: string;
  /** Markers to emit immediately before `[/size]` to close leaked-out formats. */
  innerClose: string;
  /** Markers to emit after `[/size]` to reopen any format that leaks out. */
  outerSuffix: string;
}

const EMPTY_LEAK: BoundaryFormatLeak = {
  outerPrefix: '',
  innerOpen: '',
  innerClose: '',
  outerSuffix: '',
};

function $getFirstTextDescendant(node: LexicalNode): TextNode | null {
  if ($isTextNode(node)) return node;
  if (!$isElementNode(node)) return null;
  for (const child of node.getChildren()) {
    const text = $getFirstTextDescendant(child);
    if (text) return text;
  }
  return null;
}

function $getLastTextDescendant(node: LexicalNode): TextNode | null {
  if ($isTextNode(node)) return node;
  if (!$isElementNode(node)) return null;
  const children = node.getChildren();
  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i];
    if (!child) continue;
    const text = $getLastTextDescendant(child);
    if (text) return text;
  }
  return null;
}

// Mirrors @lexical/markdown's `getTextSibling` (LexicalMarkdown.dev.js:244):
// walks transparently through inline elements to find the adjacent text node.
function $getOuterTextSibling(
  node: LexicalNode,
  backward: boolean
): TextNode | null {
  let sibling = backward ? node.getPreviousSibling() : node.getNextSibling();
  if (!sibling) {
    const parent = node.getParent();
    if (parent && $isElementNode(parent) && parent.isInline()) {
      sibling = backward
        ? parent.getPreviousSibling()
        : parent.getNextSibling();
    }
  }
  while (sibling) {
    if ($isElementNode(sibling)) {
      if (!sibling.isInline()) return null;
      const descendant = backward
        ? $getLastTextDescendant(sibling)
        : $getFirstTextDescendant(sibling);
      if (descendant) return descendant;
      sibling = backward
        ? sibling.getPreviousSibling()
        : sibling.getNextSibling();
    } else if ($isTextNode(sibling)) {
      return sibling;
    } else {
      return null;
    }
  }
  return null;
}

function hasFormat(node: TextNode | null, format: string): boolean {
  // hasFormat accepts only specific TextFormatType strings; cast at the call
  // site so the helper can stay format-agnostic.
  return (
    node?.hasFormat(format as Parameters<TextNode['hasFormat']>[0]) ?? false
  );
}

// True when `text` lives inside any FontSizeNode. Because the helpers that
// use this only call `$getOuterTextSibling`, which always walks *outside*
// the current FontSize, any FontSize ancestor of the result is necessarily
// a *different* FontSize — i.e. an adjacent block that will emit its own
// markers. We must skip our outer-marker injection in that case to avoid
// collisions like `[size=18]**X**[/size]****[size=24]**Y**[/size]`.
function $isInsideAnyFontSize(text: TextNode): boolean {
  let current: LexicalNode | null = text.getParent();
  while (current) {
    if ($isFontSizeNode(current)) return true;
    current = current.getParent();
  }
  return false;
}

/**
 * Detects formats whose markdown markers would otherwise straddle the
 * `[size=N]…[/size]` boundary, and returns the marker strings to inject so
 * the export round-trips. Returns empty strings when no formats leak.
 */
export function $detectBoundaryFormatLeak(
  node: FontSizeNode
): BoundaryFormatLeak {
  const innerFirst = $getFirstTextDescendant(node);
  const innerLast = $getLastTextDescendant(node);
  const outerPrev = $getOuterTextSibling(node, true);
  const outerNext = $getOuterTextSibling(node, false);

  if (!innerFirst && !innerLast) return EMPTY_LEAK;
  if (!outerPrev && !outerNext) return EMPTY_LEAK;

  let outerPrefix = '';
  let innerOpen = '';
  let innerClose = '';
  let outerSuffix = '';

  // Skip outer-marker injection when the adjacent text lives inside a
  // *different* FontSizeNode — that block will emit its own markers, so
  // adding ours would produce colliding doubles like `****` between
  // adjacent blocks (which Lexical's BOLD_STAR cannot reparse).
  const outerPrevInOtherFontSize =
    outerPrev !== null && $isInsideAnyFontSize(outerPrev);
  const outerNextInOtherFontSize =
    outerNext !== null && $isInsideAnyFontSize(outerNext);

  for (const [format, tag] of FORMATS_IN_OPEN_ORDER) {
    const leftLeak =
      hasFormat(outerPrev, format) && hasFormat(innerFirst, format);
    const rightLeak =
      hasFormat(outerNext, format) && hasFormat(innerLast, format);

    if (!leftLeak && !rightLeak) continue;

    // Always close the format inside `[/size]`. When `leftLeak` is set,
    // Lexical's exportTextFormat skips the inner closing because the
    // outer-prev has the format and `unclosableTags` suppresses it. When
    // `rightLeak` is set (leftLeak false), Lexical opens the inner format
    // but skips the closing because `nextNodeHasFormat` is true (the walk
    // sees the outer-next text). Either way, we must close it ourselves.
    // Inner close = reverse open order, so prepend each new tag.
    innerClose = tag + innerClose;

    if (leftLeak) {
      // Lexical's inner exportTextFormat skips opening because
      // outer-prev has the format. Open it ourselves at the start of
      // the children. Inner open uses Lexical's open order, so append.
      innerOpen = innerOpen + tag;
      if (!outerPrevInOtherFontSize) {
        // Lexical's outer-prev opens the format but skips its closing
        // because the next-text walk sees through the FontSize. Close
        // it ourselves *before* `[size=N]`. Outer close uses reverse
        // open order, so prepend.
        outerPrefix = tag + outerPrefix;
      }
    }
    if (rightLeak && !outerNextInOtherFontSize) {
      // Lexical's outer-next skips opening because `unclosedTags` still
      // has the format from the inner open. Open it ourselves *after*
      // `[/size]`. Outer reopen uses Lexical's open order, so append.
      outerSuffix = outerSuffix + tag;
    }
  }

  return { outerPrefix, innerOpen, innerClose, outerSuffix };
}
